/**
 * Export to JPG: which pages, how sharp, and how much compression.
 */

import { useState } from 'react'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { exportJpeg, parsePages, type ExportResult } from '../files/exportImages'
import { formatSize } from './CompressDialog'

interface Props {
  doc: PDFDocumentProxy
  /** The page on screen, 0-based. */
  current: number
  /** Pages selected in the thumbnail panel, 0-based. */
  selected: number[]
  stem: string
  onExported: (result: ExportResult) => void
  onClose: () => void
}

const RESOLUTIONS = [
  { dpi: 96, label: 'Screen (96 dpi)' },
  { dpi: 150, label: 'Standard (150 dpi)' },
  { dpi: 200, label: 'High (200 dpi)' },
  { dpi: 300, label: 'Print (300 dpi)' },
]

type Which = 'current' | 'selected' | 'all' | 'range'

export function ExportImagesDialog({ doc, current, selected, stem, onExported, onClose }: Props) {
  const count = doc.numPages
  const [which, setWhich] = useState<Which>(selected.length > 1 ? 'selected' : 'current')
  const [range, setRange] = useState(`1-${count}`)
  const [dpi, setDpi] = useState(150)
  const [quality, setQuality] = useState(0.9)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [error, setError] = useState('')

  const pages = which === 'current' ? [current]
    : which === 'selected' ? [...selected].sort((a, b) => a - b)
    : which === 'all' ? Array.from({ length: count }, (_, i) => i)
    : parsePages(range, count)

  // What the user will get.
  const size = (() => {
    if (!pages?.length) return null
    return pages.length === 1 ? 'one .jpg file' : `a .zip of ${pages.length} .jpg files`
  })()

  const run = async () => {
    if (!pages?.length) return
    setError('')
    setProgress({ done: 0, total: pages.length })
    try {
      const result = await exportJpeg(doc, pages, { dpi, quality, stem },
        (done, total) => setProgress({ done, total }))
      onExported(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setProgress(null)
    }
  }

  const busy = progress !== null
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="export-title">
      <form className="modal__panel" onSubmit={(e) => { e.preventDefault(); void run() }}>
        <h2 className="modal__title" id="export-title">Export to JPG</h2>
        <div className="modal__body">
          <fieldset className="export__pages" disabled={busy}>
            <legend>Pages</legend>
            <label className="check">
              <input type="radio" name="export-pages" checked={which === 'current'} onChange={() => setWhich('current')} />
              This page (page {current + 1})
            </label>
            {selected.length > 1 && (
              <label className="check">
                <input type="radio" name="export-pages" checked={which === 'selected'} onChange={() => setWhich('selected')} />
                The {selected.length} selected pages
              </label>
            )}
            <label className="check">
              <input type="radio" name="export-pages" checked={which === 'all'} onChange={() => setWhich('all')} />
              All {count} pages
            </label>
            <label className="check">
              <input type="radio" name="export-pages" checked={which === 'range'} onChange={() => setWhich('range')} />
              Pages
              <input className="export__range" value={range} aria-label="Page range, e.g. 1-3, 5"
                     onFocus={() => setWhich('range')} onChange={(e) => setRange(e.target.value)} />
            </label>
            {which === 'range' && !pages && (
              <p className="modal__hint modal__hint--warn">Type pages as 1-3, 5, 8- (this document has {count}).</p>
            )}
          </fieldset>
          <div className="field-row">
            <label className="field">
              <span>Resolution</span>
              <select value={dpi} disabled={busy} onChange={(e) => setDpi(Number(e.target.value))}>
                {RESOLUTIONS.map((r) => <option key={r.dpi} value={r.dpi}>{r.label}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Quality</span>
              <select value={quality} disabled={busy} onChange={(e) => setQuality(Number(e.target.value))}>
                <option value={0.75}>Smaller file</option>
                <option value={0.9}>Standard</option>
                <option value={0.97}>Best</option>
              </select>
            </label>
          </div>
          <p className="modal__hint">
            {size ? `You get ${size}. ` : ''}
            Images show the pages as they print, comments and form answers included.
          </p>
          {progress && (
            <p className="modal__hint" role="status">
              Exporting page {Math.min(progress.done + 1, progress.total)} of {progress.total}&hellip;
            </p>
          )}
          {error && <p className="modal__hint modal__hint--warn">{error}</p>}
        </div>
        <div className="modal__footer">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={busy || !pages?.length}>
            {busy ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </form>
    </div>
  )
}

export { formatSize }
